import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  IconButton,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
  alpha,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Select,
  SelectChangeEvent,
  Snackbar,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  WorkOutline as ProjectIcon,
  PaymentOutlined as PlanIcon,
  Add as AddIcon,
  Send as SendIcon,
  ChatBubbleOutline as PromptIcon,
  Refresh as RefreshIcon,
  Code as VariableIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Sync as SyncIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  LayersOutlined as RoundIcon,
} from '@mui/icons-material';
import companyService, { Company } from '../../services/company.service';
import companyPlanService, { CompanyPlan } from '../../services/companyPlan.service';
import projectService from '../../services/project.service';
import promptService, { Prompt } from '../../services/prompt.service';
import roundService, { Round } from '../../services/round.service';
import { Project, projectStatusColors } from '../../types/Project';
import ProjectListItem from '../../components/Projects/ProjectListItem';
import { IMG_URL, USER_FE_URL } from '../../config/config';
import { roundVariables } from '../DefaultPrompts/promptVariables';

function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      style={{ width: '100%', minWidth: 0 }}
      {...other}
    >
      {value === index && <Box sx={{ minWidth: 0 }}>{children}</Box>}
    </div>
  );
}

const CompanyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // State variables
  const [defaultPrompt, setDefaultPrompt] = useState<any>('');
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [activeRoundTab, setActiveRoundTab] = useState<number>(0);

  useEffect(() => {
    if (location?.state?.isPrompts) {
      setActiveTab(3)
      fetchCompanyProjects();
      fetchCompanyPrompts();
    }
  }, [location?.state?.isPrompt, defaultPrompt])

  // Default prompts live on their own page (sidebar → Default Prompts), so this
  // tab only shows project-wise (round-level) prompts.
  const [projectPromptSearch, setProjectPromptSearch] = useState<string>('');
  const [valuePromptTab, setValuePromptTab] = React.useState(0);
  const [selectedProjectTab, setSelectedProjectTab] = React.useState<any>();

  const handleChangePromptTab = (event: any, newValue: any) => {
    setValuePromptTab(newValue);
    setActiveRoundTab(0);
    setEditingRoundId(null);

    // Tabs map directly to projects
    if (projects && projects[newValue]) {
      const selectedProject = projects[newValue];
      setSelectedProjectTab(selectedProject);
      fetchProjectRounds(selectedProject._id);
    }
  };
  const [promptsIdByRound, setPromptsIdByRound] = useState<any>({});

  const [resetDetails, setResetDetails] = useState<any>('');
  const [resetConfiromModal, setResetConfiromModal] = useState<any>(false);

  // Prompt states
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState<boolean>(false);
  const [promptsError, setPromptsError] = useState<string | null>(null);

  // Round prompts are read-only until Edit is clicked. The backup holds the
  // text from when editing started, so Cancel can restore it.
  const [editingRoundId, setEditingRoundId] = useState<string | null>(null);
  const [roundPromptBackup, setRoundPromptBackup] = useState<string>('');

  // Round states
  const [rounds, setRounds] = useState<Record<string, Round[]>>({});
  const [roundsLoading, setRoundsLoading] = useState<Record<string, boolean>>({});
  const [roundsError, setRoundsError] = useState<Record<string, string | null>>({});
  const [roundPrompts, setRoundPrompts] = useState<Record<string, string>>({});
  const [roundPromptLoading, setRoundPromptLoading] = useState<Record<string, boolean>>({});

  // Variable menu states
  const [variableAnchorEl, setVariableAnchorEl] = useState<null | HTMLElement>(null);
  const [activeTextFieldRef, setActiveTextFieldRef] = useState<HTMLInputElement | null>(null);
  const [textFieldRefs, setTextFieldRefs] = useState<Record<string, React.RefObject<HTMLInputElement>>>({});

  // Plan and Project states
  const [plans, setPlans] = useState<CompanyPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState<boolean>(false);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState<boolean>(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState<Record<string, boolean>>({});
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null);
  const [deleteProjectDialogOpen, setDeleteProjectDialogOpen] = useState<boolean>(false);
  const [projectStatusFilter, setProjectStatusFilter] = useState<string | null>(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const getDefaultPromptList = async () => {
    try {
      const response = await promptService.getDefaultPromptList();
      setDefaultPrompt(response?.data)
    } catch (err: any) {
      console.error('Error fetching rounds:', err);
    }
  };

  useEffect(() => {
    getDefaultPromptList();
  }, [])


  // Fetch company data
  useEffect(() => {
    const fetchCompany = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const data = await companyService.getCompany(id);
        setCompany(data);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to fetch company details');
        console.error('Error fetching company:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [id]);

  // Open the Prompts tab on the first project so the editor is never blank.
  useEffect(() => {
    if (activeTab !== 3) return;
    if (projects.length === 0) return;
    if (selectedProjectTab) return;

    const firstProject = projects[0];
    setValuePromptTab(0);
    setSelectedProjectTab(firstProject);
    fetchProjectRounds(firstProject._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, projects, selectedProjectTab]);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);

    // Load data for the selected tab if it hasn't been loaded yet
    if (newValue === 1 && plans.length === 0 && !plansLoading) {
      fetchCompanyPlans();
    } else if (newValue === 2 && projects.length === 0 && !projectsLoading) {
      fetchCompanyProjects();
    } else if (newValue === 3 && prompts.length === 0 && !promptsLoading) {
      fetchCompanyProjects();
      fetchCompanyPrompts();
    }
  };

  const handleRoundTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveRoundTab(newValue);
    setEditingRoundId(null);
  };

  // Fetch company prompts
  const fetchCompanyPrompts = async () => {
    if (!id) return;

    setPromptsError(null);

    try {
      const response = await promptService.getCompanyPrompts({ "is_default_prompt": false });
      setPrompts(response.data);
    } catch (err: any) {
      setPromptsError(err?.response?.data?.message || 'Failed to fetch prompts');
      console.error('Error fetching prompts:', err);
    } finally {
      setPromptsLoading(false);
    }
  };

  // Fetch project rounds
  const fetchProjectRounds = async (projectId: string) => {
    if (!id || !projectId) return;

    setRoundsLoading(prev => ({ ...prev, [projectId]: true }));
    setRoundsError(prev => ({ ...prev, [projectId]: null }));

    try {
      const response = await roundService.getProjectRounds(id, projectId, {
        limit: 50
      });
      setRounds(prev => ({ ...prev, [projectId]: response.data }));


      const payload = {
        "type": "round",
        "company_id": id,
        "project_id": projectId,
        "round_id": ""
      }
      const responsePrompts = await promptService.getCompanyPrompts(payload);
      const promptsData = responsePrompts.data

      let promptList: any = {}
      let promptIds: any = {}
      for (let index = 0; index < promptsData.length; index++) {
        const element: any = promptsData[index];
        promptList[element?.round_id] = element?.prompt
        promptIds[element?.round_id] = element?._id
      }

      // Initialize round prompts for each round
      const initialRoundPrompts: Record<string, string> = {};
      const initialPromptIdByRoundId: any = {};
      response.data.forEach((round: Round) => {
        initialPromptIdByRoundId[round._id] = promptIds[round._id]
        initialRoundPrompts[round._id] = promptList?.[round._id] ? promptList?.[round._id] : round?.is_streaming_round ? defaultPrompt?.screeningRoundPrompt : defaultPrompt?.roundPrompt
      });
      setRoundPrompts(prev => ({ ...prev, ...initialRoundPrompts }));
      setPromptsIdByRound(initialPromptIdByRoundId);
    } catch (err: any) {
      setRoundsError(prev => ({ ...prev, [projectId]: err?.response?.data?.message || 'Failed to fetch rounds' }));
      console.error('Error fetching rounds:', err);
    } finally {
      setRoundsLoading(prev => ({ ...prev, [projectId]: false }));
    }
  };

  // Handle round prompt submission
  const handleRoundPromptSubmit = async (roundId: string) => {
    const promptText = roundPrompts[roundId];
    if (!promptText?.trim() || !id) return;

    setRoundPromptLoading(prev => ({ ...prev, [roundId]: true }));

    try {
      // Here you would call your prompt service to save the round-specific prompt
      // await promptService.addRoundPrompt(id, roundId, promptText);
      const payload = {
        "prompt": promptText,
        "type": "round",
        "company_id": id,
        "project_id": selectedProjectTab?._id,
        "round_id": roundId
      }
      await promptService.addPrompt(payload);

      if (projects && projects[valuePromptTab]) {
        const selectedProject = projects[valuePromptTab];
        fetchProjectRounds(selectedProject._id);
      }
      setEditingRoundId(null);
      showSnackbar('Round prompt saved successfully', 'success');
    } catch (err: any) {
      console.error('Error adding round prompt:', err);
      showSnackbar('Failed to save round prompt', 'error');
    } finally {
      setRoundPromptLoading(prev => ({ ...prev, [roundId]: false }));
    }
  };

  // Handle round prompt change
  const handleRoundPromptChange = (roundId: string, value: string) => {
    setRoundPrompts(prev => ({ ...prev, [roundId]: value }));
  };

  // --- Read-only / edit mode for a round prompt ---
  const startEditingRoundPrompt = (roundId: string) => {
    setRoundPromptBackup(roundPrompts[roundId] || '');
    setEditingRoundId(roundId);
  };

  const cancelEditingRoundPrompt = (roundId: string) => {
    setRoundPrompts(prev => ({ ...prev, [roundId]: roundPromptBackup }));
    setEditingRoundId(null);
  };

  const handleVariableMenuOpen = (event: React.MouseEvent<HTMLElement>, textFieldRef: HTMLInputElement | null) => {
    setVariableAnchorEl(event.currentTarget);
    setActiveTextFieldRef(textFieldRef);
  };

  const handleVariableMenuClose = () => {
    setVariableAnchorEl(null);
    setActiveTextFieldRef(null);
  };

  const insertVariable = (variable: string) => {
    if (activeTextFieldRef) {
      const input = activeTextFieldRef;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = input.value;

      const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);

      // The round editor tags its input with the round it belongs to
      const roundId = input.getAttribute('data-round-id');

      if (roundId) {
        setRoundPrompts(prev => ({ ...prev, [roundId]: newValue }));

        // Set focus back to the input and position cursor after the inserted variable
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(start + variable.length, start + variable.length);
        }, 0);
      }
    }
    handleVariableMenuClose();
  };

  // Create refs for text fields
  const getTextFieldRef = (roundId: string) => {
    if (!textFieldRefs[roundId]) {
      setTextFieldRefs((prev: any) => ({
        ...prev,
        [roundId]: React.createRef<HTMLInputElement>()
      }));
    }
    return textFieldRefs[roundId];
  };

  // Dialog handlers
  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  // Delete company
  const handleDeleteCompany = async () => {
    if (!id) return;

    try {
      await companyService.deleteCompany(id);
      navigate('/companies');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete company');
      console.error('Error deleting company:', err);
      closeDeleteDialog();
    }
  };

  // Fetch company plans
  const fetchCompanyPlans = async () => {
    if (!id) return;

    setPlansLoading(true);
    setPlansError(null);

    try {
      const response = await companyPlanService.getCompanyPlans(id);
      setPlans(response.data);
    } catch (err: any) {
      setPlansError(err?.response?.data?.message || 'Failed to fetch company plans');
      console.error('Error fetching company plans:', err);
    } finally {
      setPlansLoading(false);
    }
  };

  // Fetch company projects
  const fetchCompanyProjects = async () => {
    if (!id) return;

    setProjectsLoading(true);
    setProjectsError(null);

    try {
      const response = await projectService.getCompanyProjects(id, {
        limit: 50 // Get a larger number of projects to display
      });
      setProjects(response.data);
    } catch (err: any) {
      setProjectsError(err?.response?.data?.message || 'Failed to fetch company projects');
      console.error('Error fetching company projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  };

  // Handle project status update
  const handleUpdateProjectStatus = async (projectId: string, newStatus: string) => {
    if (!id) return;

    // Mark this project's status update as loading
    setStatusUpdateLoading(prev => ({ ...prev, [projectId]: true }));

    try {
      await projectService.updateProjectStatus(id, projectId, newStatus as any);

      // Update the project in the local state
      setProjects(prev =>
        prev.map(project =>
          project._id === projectId
            ? { ...project, status: newStatus as any }
            : project
        )
      );
    } catch (err) {
      console.error('Error updating project status:', err);
      // Could add a snackbar or other error notification here
    } finally {
      // Mark this project's status update as no longer loading
      setStatusUpdateLoading(prev => ({ ...prev, [projectId]: false }));
    }
  };

  // Helper function to render status chip
  const renderStatusChip = (status: string) => {
    let color: 'success' | 'error' | 'warning' | 'default' = 'default';

    switch (status) {
      case 'active':
        color = 'success';
        break;
      case 'inactive':
        color = 'error';
        break;
      case 'pending':
        color = 'warning';
        break;
      default:
        color = 'default';
    }

    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={color}
        size="small"
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };

  // Company Plans Tab Content
  const renderPlansTab = () => {
    if (plansLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (plansError) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {plansError}
        </Alert>
      );
    }

    if (plans.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            No plans found for this company
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            color="primary"
            sx={{ mt: 1 }}
            onClick={() => navigate(`/companies/${id}/plans/new`)}
          >
            Add Plan
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            color="primary"
            onClick={() => navigate(`/companies/${id}/plans/new`)}
          >
            Add Plan
          </Button>
        </Box>

        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid item xs={12} md={6} key={plan._id}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 2,
                  border: plan.isActive
                    ? `2px solid ${theme.palette.success.main}`
                    : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {typeof plan.plan === 'object' ? plan.plan.name : 'Plan'}
                    </Typography>
                    <Chip
                      label={plan.isActive ? 'Active' : 'Inactive'}
                      color={plan.isActive ? 'success' : 'default'}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Start Date</Typography>
                      <Typography variant="body1">
                        {new Date(plan.startDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">End Date</Typography>
                      <Typography variant="body1">
                        {new Date(plan.endDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Payment Status</Typography>
                      <Chip
                        label={plan.paymentStatus}
                        color={
                          plan.paymentStatus === 'paid' ? 'success' :
                            plan.paymentStatus === 'pending' ? 'warning' :
                              plan.paymentStatus === 'failed' ? 'error' : 'default'
                        }
                        size="small"
                        sx={{ textTransform: 'capitalize', mt: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Price</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ${typeof plan.plan === 'object' ? plan.plan.price : 0}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => { /* Handle view plan details */ }}
                    >
                      Details
                    </Button>
                    {!plan.isActive && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => { /* Handle activate plan */ }}
                      >
                        Activate
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  function a11yProps(index: number) {
    return {
      id: `vertical-tab-${index}`,
      'aria-controls': `vertical-tabpanel-${index}`,
    };
  }

  const [syncLoading, setSyncLoading] = useState(false);

  const handleSyncBraintrust = async () => {
    setSyncLoading(true);
    try {
      await promptService.updatePrompts();

      // Refresh the rounds of whichever project is open
      if (projects && projects[valuePromptTab]) {
        const selectedProject = projects[valuePromptTab];
        fetchProjectRounds(selectedProject._id);
      }
      showSnackbar('Prompts synced with Braintrust successfully', 'success');
    } catch (err) {
      console.error('Error syncing with braintrust:', err);
      showSnackbar('Failed to sync with Braintrust', 'error');
    } finally {
      setSyncLoading(false);
    }
  };

  const renderLoadingState = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <CircularProgress />
    </Box>
  );

  const renderEmptyState = (title: string, description: string, icon: React.ReactNode) => (
    <Box
      sx={{
        textAlign: 'center',
        py: 6,
        px: 3,
        borderRadius: 3,
        border: `1px dashed ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.text.primary, 0.015),
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.palette.text.secondary,
          bgcolor: alpha(theme.palette.text.primary, 0.05),
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  );

  // Project list on the left of the prompt editor
  const promptSidebarSx = {
    minWidth: { xs: '100%', md: 240 },
    maxWidth: { xs: '100%', md: 240 },
    maxHeight: { xs: 220, md: 620 },
    '& .MuiTab-root': {
      alignItems: 'flex-start',
      textAlign: 'left',
      textTransform: 'none',
      fontWeight: 500,
      minHeight: 44,
      px: 2,
      borderRadius: 1.5,
      mb: 0.5,
      '&.Mui-selected': {
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        fontWeight: 700,
      },
    },
    '& .MuiTabs-indicator': { display: { xs: 'none', md: 'block' } },
  } as const;

  const promptFieldSx = {
    '& .MuiInputBase-root': {
      bgcolor: alpha(theme.palette.text.primary, 0.02),
      alignItems: 'flex-start',
    },
    '& .MuiInputBase-inputMultiline': {
      resize: 'vertical',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: 13,
      lineHeight: 1.7,
    },
  } as const;

  // Read-only view of a prompt. Same size and font as the editor, so switching
  // to Edit doesn't make the text jump.
  const renderPromptViewer = (value: string, emptyText: string) => (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.text.primary, 0.02),
        px: 1.75,
        py: 1.5,
        height: 620,
        overflowY: 'auto',
      }}
    >
      {value?.trim() ? (
        <Typography
          component="pre"
          sx={{
            m: 0,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: 13,
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {emptyText}
        </Typography>
      )}
    </Box>
  );

  // Company Prompt Tab Content (project-wise prompts only)
  const renderPromptTab = () => {
    if (promptsLoading || projectsLoading) {
      return renderLoadingState();
    }

    if (promptsError) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {promptsError}
        </Alert>
      );
    }

    // The search box filters the list, but each Tab keeps its original index
    // as its value so the index-based handlers stay correct.
    const visibleProjects = projects
      .map((item: any, index: number) => ({ item, index }))
      .filter(({ item }: any) => {
        if (!projectPromptSearch.trim()) return true;
        return (item?.project_name || '')
          .toLowerCase()
          .includes(projectPromptSearch.trim().toLowerCase());
      });

    const sidebarValue = visibleProjects.some(({ index }) => index === valuePromptTab)
      ? valuePromptTab
      : false;

    return (
      <Card
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, '&:hover': { transform: 'none' } }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <PromptIcon sx={{ color: theme.palette.primary.main }} />
              <Typography variant="subtitle1" fontWeight={700}>
                Project Wise Prompts
              </Typography>
              {projects.length > 0 && (
                <Chip size="small" label={projects.length} sx={{ fontWeight: 600 }} />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Round-level prompts for this company. Global templates live under Default Prompts.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={syncLoading ? undefined : <SyncIcon />}
            onClick={handleSyncBraintrust}
            disabled={syncLoading}
            sx={{ minWidth: 170 }}
          >
            {syncLoading ? <CircularProgress size={20} /> : 'Sync with Braintrust'}
          </Button>
        </Box>

        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {projectsError ? (
            <Alert severity="error">{projectsError}</Alert>
          ) : projects.length === 0 ? (
            renderEmptyState(
              'No projects to configure',
              'Prompts can be tailored per round once this company has projects.',
              <ProjectIcon sx={{ fontSize: 32 }} />
            )
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 3 },
                alignItems: { xs: 'flex-start', md: 'stretch' },
              }}
            >
              {/* Project picker */}
              <Box
                sx={{
                  width: { xs: '100%', md: 260 },
                  flexShrink: 0,
                  borderRight: { xs: 0, md: `1px solid ${theme.palette.divider}` },
                  pr: { xs: 0, md: 2 },
                }}
              >
                {projects.length > 5 && (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search projects"
                    value={projectPromptSearch}
                    onChange={(e) => setProjectPromptSearch(e.target.value)}
                    sx={{ mb: 1.5 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                {visibleProjects.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
                    No project matches "{projectPromptSearch}".
                  </Typography>
                ) : (
                  <Tabs
                    orientation={isSmallScreen ? 'horizontal' : 'vertical'}
                    variant="scrollable"
                    scrollButtons="auto"
                    value={sidebarValue}
                    onChange={handleChangePromptTab}
                    aria-label="Project tabs"
                    sx={promptSidebarSx}
                  >
                    {visibleProjects.map(({ item, index }: any) => (
                      <Tab
                        key={item._id}
                        value={index}
                        label={
                          <Box sx={{ textTransform: 'capitalize', textAlign: 'left', width: '100%' }}>
                            {item?.project_name}
                          </Box>
                        }
                        {...a11yProps(index)}
                      />
                    ))}
                  </Tabs>
                )}
              </Box>

              {projects.map((item: any, index: number) => {
                const projectRounds = rounds[item._id] || [];
                const isRoundsLoading = roundsLoading[item._id] || false;
                const roundsErrorMsg = roundsError[item._id];

                return (
                  <TabPanel key={item._id} value={valuePromptTab} index={index}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Typography variant="h6" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                        {item.project_name}
                      </Typography>
                      {projectRounds.length > 0 && (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<RoundIcon fontSize="small" />}
                          label={`${projectRounds.length} round${projectRounds.length === 1 ? '' : 's'}`}
                        />
                      )}
                    </Box>

                    {isRoundsLoading ? (
                      renderLoadingState()
                    ) : roundsErrorMsg ? (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {roundsErrorMsg}
                      </Alert>
                    ) : projectRounds.length === 0 ? (
                      renderEmptyState(
                        'No rounds in this project',
                        'Add interview rounds to this project to configure their prompts.',
                        <RoundIcon sx={{ fontSize: 32 }} />
                      )
                    ) : (
                      <Box>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                          <Tabs
                            value={activeRoundTab}
                            onChange={handleRoundTabChange}
                            aria-label="project round details tabs"
                            variant="scrollable"
                            scrollButtons="auto"
                          >
                            {projectRounds.map((round: Round) => (
                              <Tab
                                key={round._id}
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {/* Filled dot = this round has its own saved prompt */}
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        bgcolor: promptsIdByRound[round._id]
                                          ? theme.palette.primary.main
                                          : alpha(theme.palette.text.primary, 0.2),
                                      }}
                                    />
                                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {round.round_title}
                                    </Box>
                                  </Box>
                                }
                                sx={{ minHeight: 48, textTransform: 'none', maxWidth: '220px' }}
                              />
                            ))}
                          </Tabs>
                        </Box>

                        {projectRounds.map((round: Round, roundIndex: number) => {
                          if (roundIndex !== activeRoundTab) return null;

                          const isEditingRound = editingRoundId === round._id;
                          const hasCustomPrompt = Boolean(promptsIdByRound[round._id]);
                          const isSaving = roundPromptLoading[round._id] || false;

                          return (
                            <Box key={round._id}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  mb: 2,
                                  pb: 2,
                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                  <Typography variant="subtitle1" fontWeight={700}>
                                    {round.round_title}
                                  </Typography>
                                  {/* Shows at a glance whether this round overrides the default */}
                                  <Chip
                                    size="small"
                                    variant={hasCustomPrompt ? 'filled' : 'outlined'}
                                    color={hasCustomPrompt ? 'primary' : 'default'}
                                    label={hasCustomPrompt ? 'Custom prompt' : 'Using default'}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {round.is_ai && <Chip label="Mobla AI" color="success" size="small" />}
                                  {round.is_ongoing_round && <Chip label="Ongoing" color="primary" size="small" />}
                                  {round.is_streaming_round && <Chip label="Streaming" color="secondary" size="small" />}
                                </Box>
                              </Box>

                              {/* Toolbar: context on the left, actions on the right */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  mb: 2,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {isEditingRound
                                      ? 'Enter a prompt for this round.'
                                      : hasCustomPrompt
                                        ? 'This prompt is used for this round.'
                                        : 'Showing the default prompt. Editing it creates an override for this round.'}
                                  </Typography>
                                  {isEditingRound && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<VariableIcon />}
                                      endIcon={<ArrowDownIcon />}
                                      onClick={(e) => handleVariableMenuOpen(e, textFieldRefs[round._id]?.current || null)}
                                    >
                                      Variables
                                    </Button>
                                  )}
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {isEditingRound ? (
                                    <>
                                      <Button
                                        variant="outlined"
                                        color="inherit"
                                        startIcon={<CloseIcon />}
                                        onClick={() => cancelEditingRoundPrompt(round._id)}
                                        disabled={isSaving}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={isSaving ? undefined : <SendIcon />}
                                        onClick={() => handleRoundPromptSubmit(round._id)}
                                        disabled={!(roundPrompts[round._id]?.trim()) || isSaving}
                                        sx={{ minWidth: 110 }}
                                      >
                                        {isSaving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Tooltip
                                        title={hasCustomPrompt
                                          ? 'Remove this round\'s custom prompt and go back to the default'
                                          : 'This round already uses the default prompt'}
                                      >
                                        <span>
                                          <Button
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<RefreshIcon />}
                                            onClick={() => handleResetPrompt(round._id)}
                                            disabled={!hasCustomPrompt}
                                          >
                                            Reset
                                          </Button>
                                        </span>
                                      </Tooltip>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<EditIcon />}
                                        onClick={() => startEditingRoundPrompt(round._id)}
                                        sx={{ minWidth: 110 }}
                                      >
                                        Edit
                                      </Button>
                                    </>
                                  )}
                                </Box>
                              </Box>

                              {isEditingRound ? (
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={20}
                                  placeholder={`Enter prompt for ${round.round_title}...`}
                                  value={roundPrompts[round._id] || ''}
                                  onChange={(e) => handleRoundPromptChange(round._id, e.target.value)}
                                  disabled={isSaving}
                                  inputRef={getTextFieldRef(round._id)}
                                  inputProps={{ 'data-round-id': round._id }}
                                  sx={promptFieldSx}
                                />
                              ) : (
                                renderPromptViewer(
                                  roundPrompts[round._id] || '',
                                  'No prompt set for this round yet. Click Edit to add one.'
                                )
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </TabPanel>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Company Projects Tab Content
  const renderProjectsTab = () => {
    if (projectsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (projectsError) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {projectsError}
        </Alert>
      );
    }

    if (projects.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            No projects found for this company
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            color="primary"
            sx={{ mt: 1 }}
            onClick={() => navigate(`/companies/${id}/projects/new`)}
          >
            Add Project
          </Button>
        </Box>
      );
    }

    // Group projects by status
    const projectsByStatus = projects.reduce((acc: Record<string, any[]>, project) => {
      if (!acc[project.status]) {
        acc[project.status] = [];
      }
      acc[project.status].push(project);
      return acc;
    }, {});

    // Order of status display
    const statusOrder = ['active', 'paused', 'draft', 'closed', 'archived'];

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight="medium">
              Projects ({projects.length})
            </Typography>
          </Box>
          {/* <Button
            variant="contained"
            startIcon={<AddIcon />}
            color="primary"
            onClick={() => navigate(`/companies/${id}/projects/new`)}
          >
            Add Project
          </Button> */}
        </Box>

        {/* Status based tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={projectStatusFilter === null ? 0 : statusOrder.indexOf(projectStatusFilter as any) + 1}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="project status tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              label={`All (${projects.length})`}
              onClick={() => setProjectStatusFilter(null)}
              sx={{ textTransform: 'none' }}
            />
            {statusOrder.filter(status => projectsByStatus[status]?.length > 0).map((status) => (
              <Tab
                key={status}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: projectStatusColors[status],
                      }}
                    />
                    {`${status.charAt(0).toUpperCase() + status.slice(1)} (${projectsByStatus[status]?.length || 0})`}
                  </Box>
                }
                onClick={() => setProjectStatusFilter(status)}
                sx={{ textTransform: 'none' }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Project List */}
        <Box>
          {projects
            .filter(project => projectStatusFilter === null || project.status === projectStatusFilter)
            .map((project) => (
              <Box key={project._id} mb={2}>
                <ProjectListItem
                  project={project}
                  onView={(id) => {
                    window.open(USER_FE_URL + "job-details/" + id, "_blank")
                  }}
                  // onView={(id) => navigate(`/companies/${company?._id}/projects/${id}`)}
                  onEdit={(id) => navigate(`/companies/${company?._id}/projects/${id}/edit`)}
                  onDelete={(id) => {
                    setDeleteProjectId(id);
                    setDeleteProjectDialogOpen(true);
                  }}
                  onUpdateStatus={handleUpdateProjectStatus}
                />
              </Box>
            ))}
        </Box>

        {/* Delete Project Dialog */}
        <Dialog
          open={deleteProjectDialogOpen}
          onClose={() => setDeleteProjectDialogOpen(false)}
          aria-labelledby="delete-project-dialog-title"
          aria-describedby="delete-project-dialog-description"
        >
          <DialogTitle id="delete-project-dialog-title">Confirm Delete Project</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-project-dialog-description">
              Are you sure you want to delete this project? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteProjectDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!deleteProjectId || !id) return;

                try {
                  await projectService.deleteProject(id, deleteProjectId);
                  setProjects(prevProjects =>
                    prevProjects.filter(project => project._id !== deleteProjectId)
                  );
                  setDeleteProjectDialogOpen(false);
                } catch (err) {
                  console.error('Error deleting project:', err);
                }
              }}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Reset removes a round's custom prompt, so it falls back to the default
  const handleResetPrompt = (roundId: string) => {
    setResetDetails({ id: roundId });
    setResetConfiromModal(true);
  }

  const handleConfirmReset = async () => {
    try {
      await promptService.getResetPrompt(promptsIdByRound[resetDetails?.id]);
      setEditingRoundId(null);
      if (projects && projects[valuePromptTab]) {
        fetchProjectRounds(projects[valuePromptTab]._id);
      }
      showSnackbar('Prompt reset to default', 'success');
    } catch (err: any) {
      console.error('Error resetting round prompt:', err);
      showSnackbar('Failed to reset prompt', 'error');
    }
    setResetConfiromModal(false);
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/companies')}
        >
          Back to Companies
        </Button>
      </Box>
    );
  }

  if (!company) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Company not found
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/companies')}
        >
          Back to Companies
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with back button and actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          onClick={() => navigate('/companies')}
          sx={{ mr: 1 }}
          aria-label="Back to companies"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Company Details
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit Company">
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/companies/edit/${id}`)}
            >
              Edit
            </Button>
          </Tooltip>
          <Tooltip title="Delete Company">
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={openDeleteDialog}
            >
              Delete
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Company Name and Status */}
      <Card
        elevation={3}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          boxShadow: `0 4px 20px 0 ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {company?.logo ?
                  <img src={IMG_URL + company?.logo} style={{ marginRight: 3, borderRadius: "50%", height: "40px", width: "40px" }} alt='' />
                  :
                  <BusinessIcon sx={{ mr: 1, color: theme.palette.primary.main, fontSize: 32 }} />
                }
                <Typography variant="h4" fontWeight="bold">
                  {company.name}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              {renderStatusChip(company.status)}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="company details tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            label="Company Info"
            icon={<BusinessIcon />}
            iconPosition="start"
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
          <Tab
            label="Plans"
            icon={<PlanIcon />}
            iconPosition="start"
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
          <Tab
            label="Projects"
            icon={<ProjectIcon />}
            iconPosition="start"
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
          <Tab
            label="Prompts"
            icon={<PromptIcon />}
            iconPosition="start"
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                  Contact Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
                    <Box>
                      <Typography variant="body2" color="textSecondary">Email</Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                        {company.email_id}
                        {company.is_email_verify && (
                          <Chip size="small" color="success" label="Verified" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PhoneIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
                    <Box>
                      <Typography variant="body2" color="textSecondary">Phone</Typography>
                      <Typography variant="body1">
                        {company.country_code ? `${company.country_code} ${company.contact_no}` : company.contact_no}
                      </Typography>
                    </Box>
                  </Box>

                  {company.website && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LanguageIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
                      <Box>
                        <Typography variant="body2" color="textSecondary">Website</Typography>
                        <Typography
                          variant="body1"
                          component="a"
                          href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {company.website}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* {company.address && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOnIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
                      <Box>
                        <Typography variant="body2" color="textSecondary">Address</Typography>
                        <Typography variant="body1">{company.address}</Typography>
                      </Box>
                    </Box>
                  )} */}

                  {company.contactPerson && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
                      <Box>
                        <Typography variant="body2" color="textSecondary">Contact Person</Typography>
                        <Typography variant="body1">{company.contactPerson}</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Company Information */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Company Information
                  </Typography>
                  {/* <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/companies/${id}/projects/new`)}
                  >
                    Add Project
                  </Button> */}
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {company.industry && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">Industry</Typography>
                      <Typography variant="body1">{company.industry}</Typography>
                    </Box>
                  )}

                  {company.about_us && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">About Us</Typography>
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {company.about_us}
                      </Typography>
                    </Box>
                  )}

                  {company.founded_date && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">Founded Date</Typography>
                      <Typography variant="body1">
                        {new Date(company.founded_date).toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </Typography>
                    </Box>
                  )}

                  {company.company_size && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">Company Size</Typography>
                      <Typography variant="body1">
                        {company.company_size}
                      </Typography>
                    </Box>
                  )}

                  {company.video_link && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">Video Link</Typography>
                      <Typography
                        variant="body1"
                        component="a"
                        href={company.video_link.startsWith('http') ? company.video_link : `https://${company.video_link}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {company.video_link}
                      </Typography>
                    </Box>
                  )}

                  {company?.social_media_profiles?.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">Social Media Profiles</Typography>
                      <Box sx={{ mt: 1 }}>
                        {company?.social_media_profiles.map((profile: any, index: number) => (
                          <Chip
                            key={index}
                            label={profile.platform}
                            component="a"
                            href={profile.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            clickable
                            color="primary"
                            variant="outlined"
                            sx={{ mr: 1, mb: 1 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {company.policy && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">Policy</Typography>
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {company.policy}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" color="textSecondary">Created At</Typography>
                    <Typography variant="body1">
                      {company.createdAt ? new Date(company.createdAt).toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      }) : 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="textSecondary">Last Updated</Typography>
                    <Typography variant="body1">
                      {company.updatedAt ? new Date(company.updatedAt).toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      }) : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Plans Tab */}
      {activeTab === 1 && renderPlansTab()}

      {/* Projects Tab */}
      {activeTab === 2 && renderProjectsTab()}

      {/* Prompts Tab */}
      {activeTab === 3 && renderPromptTab()}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the company "{company.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteCompany} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetConfiromModal} onClose={() => setResetConfiromModal(false)}>
        <DialogTitle>Confirm Reset</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to Reset this Prompt? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfiromModal(false)}>Cancel</Button>
          <Button onClick={handleConfirmReset} color="error" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Variable Insertion Menu */}
      <Menu
        anchorEl={variableAnchorEl}
        open={Boolean(variableAnchorEl)}
        onClose={handleVariableMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {roundVariables.map((variable, index) => (
          <MenuItem key={index} onClick={() => insertVariable(variable.value)}>
            <ListItemIcon>
              <VariableIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={variable.name} secondary={variable.value} />
          </MenuItem>
        ))}
      </Menu>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyDetails;